import { useRouter } from 'next/router';
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import useAfterRenderCallback from 'src/common/hooks/useAfterRenderCallback';
import useMobileNativeWebView from 'src/common/hooks/useMobileNativeWebView';
import { localStore } from 'src/common/utils/storageWrapper';
import Notification from '../../common/components/Notification';
import {
  GEOLOCATION_COOKIE,
  GEOLOCATION_ERROR_COOKIE,
  GEOLOCATION,
  LOCATION_PROMPT,
  MOBILE_NATIVE_WEBVIEW_LAT,
  MOBILE_NATIVE_WEBVIEW_LONG,
} from '../../common/constants';
import useCookie from '../../common/hooks/useCookie';
import useTimeout from '../../common/hooks/useTimeout';

const geolocationErrorKeys: GeolocationError[] = ['geolocation-error--permission-denied', 'geolocation-error--position-unavailable', 'geolocation-error--timeout'];

const geolocationCookieAttributes = { expires: 30, path: '/' };
const geolocationErrorCookieAttributes = { path: '/' };

type GeolocationError =
 | 'geolocation-error--permission-denied'
 | 'geolocation-error--position-unavailable'
 | 'geolocation-error--timeout'
 | 'geolocation-error--unknown'
 | null;

 type WebViewLocalStorageError = 'mobile-native-coords-error--missing-or-invalid' | 'mobile-native-coords--error-get-localStorage';

const defaultOptions = {
  enableHighAccuracy: true,
  timeout: 5000,
  maximumAge: 0,
};

export type GeolocationCoords = Pick<GeolocationCoordinates, 'latitude' | 'longitude'>;

type UseGeolocation = {
  /** Coords returned by `navigator.geolocation.getCurrentPosition` */
  coords?: GeolocationCoords;
  /** Shows toast notification */
  error?: GeolocationError | WebViewLocalStorageError;
  /** Only shows if geoLocation permission cookie is undefined and is not WebView. */
  showPrompt: boolean;
  dismissModal?: () => void;
  /** Sets cookie `gmb_geolocation: true` */
  handleLocationPromptAccept?: () => void;
  /** Sets cookie `gmb_geolocation: false` */
  handleLocationPromptDecline?: () => void;
};

const useGeolocation = (isNearbySearch: boolean): UseGeolocation => {
  const { isMobileNativeWebView } = useMobileNativeWebView();
  const [geolocationCookie, setGeolocationCookie] = useCookie(GEOLOCATION_COOKIE);
  const [geolocationErrorCookie, setGeolocationErrorCookie] = useCookie(GEOLOCATION_ERROR_COOKIE);
  const [showPrompt, setShowLocationPrompt] = useState(false);
  const [coords, setCoords] = useState<GeolocationCoords>();
  const [error, setError] = useState<GeolocationError | WebViewLocalStorageError>(null);
  const router = useRouter();

  const dismissModal = useAfterRenderCallback(() => setShowLocationPrompt(false));

  const handleLocationPromptAccept = useAfterRenderCallback(() => {
    dismissModal();
    setGeolocationCookie('true', geolocationCookieAttributes);

    gtag('event', LOCATION_PROMPT, {
      event_category: 'Search',
      event_label: 'Accepted',
    });
  });

  const handleLocationPromptDecline = useAfterRenderCallback(() => {
    dismissModal();
    setGeolocationCookie('false', geolocationCookieAttributes);

    gtag('event', LOCATION_PROMPT, {
      event_category: 'Search',
      event_label: 'Declined',
    });
  });

  const dismissError = useAfterRenderCallback(() => {
    setError(null);
    // Don't show cookie again this session
    setGeolocationErrorCookie('true', geolocationErrorCookieAttributes);
  });

  const handleSuccess = useAfterRenderCallback((position: GeolocationPosition) => {
    gtag('event', GEOLOCATION, {
      event_category: 'Search',
      event_label: 'Success',
    });

    setCoords(position.coords);
  });

  const handleError = useAfterRenderCallback((positionError: GeolocationPositionError) => {
    setError(geolocationErrorKeys[positionError.code - 1] ?? 'geolocation-error--unknown');

    gtag('event', GEOLOCATION, {
      event_category: 'Search',
      event_label: 'Failure',
    });
  });

  const getCurrentPosition = useAfterRenderCallback(() => {
    if (isMobileNativeWebView) {
      // Get coordinates from localStorage where they are set by the mobile native app
      try {
        /** Number returns falsy `0` for `null` item or empty `` */
        const [latitude, longitude] = [
          Number(localStore.getItem(MOBILE_NATIVE_WEBVIEW_LAT)),
          Number(localStore.getItem(MOBILE_NATIVE_WEBVIEW_LONG)),
        ];

        if (!latitude || !longitude) {
          setError('mobile-native-coords-error--missing-or-invalid'); // set improperly by native team
          return;
        }
        setCoords({ latitude, longitude });
        return;
      } catch (e) {
        setError('mobile-native-coords--error-get-localStorage'); // retreival error, such as localStorage full
        return;
      }
    }
    if (typeof window !== 'undefined') {
      navigator.geolocation.getCurrentPosition(handleSuccess, handleError, defaultOptions);
    }
  });

  useEffect(() => {
    if (!!error && geolocationErrorCookie !== 'true') {
      toast.dismiss();
      toast(
        <Notification
          iconId="warning"
          translation="search-page"
          headingKey={`${error}--heading`}
          messageKey="geolocation-error--message"
          action={router.reload}
        />,
        { onClose: dismissError, autoClose: false },
      );
    }
  }, [error, geolocationErrorCookie, dismissError, router.reload]);

  const canShowGeolocationPrompt = (
    typeof window !== 'undefined'
    && isNearbySearch
    && 'geolocation' in navigator
    && typeof geolocationCookie === 'undefined'
  );

  useTimeout(
    () => setShowLocationPrompt(true),
    300,
    canShowGeolocationPrompt,
  );

  useEffect(() => {
    if (geolocationCookie === 'true' && isNearbySearch) getCurrentPosition();
  }, [geolocationCookie, isNearbySearch, getCurrentPosition, isMobileNativeWebView]);

  return {
    coords,
    error,
    showPrompt: showPrompt && !isMobileNativeWebView,
    dismissModal,
    handleLocationPromptAccept,
    handleLocationPromptDecline,
  };
};

export default useGeolocation;
